import { withPropsOnChange } from 'recompose';
import hash from 'object-hash';
import spaceTime from 'spacetime';

/**
 * Acts like withPropsOnChange but shares the change check globally
 * so using one of these in different files with the same props will use the cached result
 * @param {*} cacheKeys
 * @param {*} withPropsFunction
 */
export const withPropsOnGlobalChange = (propsKeys, propsFunction) => {
  let lastPropsHash;
  let cachedResult;

  return withPropsOnChange(propsKeys, props => {
    const propsHash = hash.MD5(
      propsKeys.reduce((pickedProps, key) => {
        pickedProps[key] = props[key];
        return pickedProps;
      }, {})
    );
    if (propsHash !== lastPropsHash) {
      const result = propsFunction(props);
      cachedResult = result;
      //console.log('Got new result!', cachedResult);
      lastPropsHash = propsHash;
    } else {
      //console.log('Got cached result!', cachedResult);
    }
    return cachedResult;
  });
};

export const clearMultipleLocalStorage = (...keys) => {
  keys.forEach(key => localStorage.removeItem(key));
};

/**
 * This formatNumber function is to re-format number to currency.
 * @param {number} number - Number
 * @param return - Array of currency, e.g ["3,334,440,233", ".", "33"]
 */

export const formatNumber = number => {
  const val = number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

  return val.split(/(\.+)/).filter(Boolean);
};

export function getOrdinal(daydate) {
  const s = ['th', 'st', 'nd', 'rd'],
    v = daydate % 100;
  return daydate.toString() + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function getOrdinalOnly(daydate) {
  const s = ['th', 'st', 'nd', 'rd'],
    v = daydate % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export const formatMoneyWithPrefix = (amount, prefix) => {
  const cleanString = amount.toFixed(2).toString();
  if (amount < 0) {
    return `-$${cleanString.slice(1)}`;
  }
  return `$${cleanString}`;
};

export const formatMoney = (amount, decimalCount = 2, decimal = '.', thousands = ',') => {
  try {
    decimalCount = Math.abs(decimalCount);
    decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

    const negativeSign = amount < 0 ? '-' : '';

    let i = parseInt((amount = Math.abs(Number(amount) || 0).toFixed(decimalCount))).toString();
    let j = i.length > 3 ? i.length % 3 : 0;

    return (
      negativeSign +
      (j ? i.substr(0, j) + thousands : '') +
      i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousands) +
      (decimalCount
        ? decimal +
          Math.abs(amount - i)
            .toFixed(decimalCount)
            .slice(2)
        : '')
    );
  } catch (e) {
    console.log(e);
  }
};

export function aOrAn(string) {
  if (string.charAt(0).match(/[aeiouAEIOU]/g)) {
    return `an ${string}`;
  } else {
    return `a ${string}`;
  }
}

export function constructAddress(location) {
  if (location) {
    var multilineAddress = '';

    if (location.unit) {
      multilineAddress += location.unit + ' / ';
    }

    if (location.street) {
      multilineAddress += location.street + ',\n';
    }

    if (location.district && location.district != location.region) {
      multilineAddress += location.district + ',\n';
    }

    if (location.region) {
      multilineAddress += location.region + ',\n';
    }

    if (location.post_code) {
      multilineAddress += location.post_code + ',\n';
    }

    multilineAddress += 'New Zealand'; // Country will always be New Zealand

    return multilineAddress;
  }
}

/**
 * Returns a formatted string of item/car data
 * @param {*} riskItemOrCar
 * @param {string} fidelity how detailed the data should be,
 * @param {string} separateor how to seperate parts, default: ' '
 * Values:
 *   - FULL: all fields
 *   - DEFAULT: year, make, model, sub_model --- make, family, series, model (same as full for phone)
 * Designed for more to be added as required
 */
export function formatRisk(coverage, fidelity = 'DEFAULT', seperator = ' ') {
  // Extract product type from coverage'
  console.log('coverage is: ', coverage);
  let productType;
  if (coverage) {
    if (coverage.pet) {
      productType = 'pet';
      coverage = coverage.pet;
    }
    if (coverage.vehicle) {
      productType = 'vehicle';
      coverage = coverage.vehicle;
    }
    if (coverage.item) {
      productType = 'item';
      coverage = coverage.item;
    }
  }
  console.log('product type is: ', productType);

  let parts;
  let {
    year,
    make,
    family,
    series,
    model,
    sub_model,
    variant,
    breed,
    species,
    gender,
    pet_name
  } = coverage;

  // Tweak parts based on fidelity setting
  switch (fidelity) {
    case 'FULL':
      break; // Full means keep everything
    default:
      variant = '';
      break;
  }

  if (productType == 'vehicle') parts = [year, make, model, sub_model, variant];
  if (productType == 'item') parts = [make, family, series, model];
  if (productType == 'pet') parts = [pet_name, breed];

  return parts.filter(part => part && part !== 'Other').join(seperator);
}

export function removeStringOther(model) {
  model = model && model !== 'Other' ? model + '\n' : '';
  return model;
}

export const numberWithCommas = x => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const convertClaimYearToClaimDate = claimYear => {
  let todayDate = spaceTime.now();

  if (spaceTime.now().format('yyyy') == claimYear) {
    todayDate = todayDate.subtract(1, 'day');
  }

  let buildClaimDate = `${claimYear}-${todayDate.format('MM')}-${todayDate.format('dd')}`;
  if (!spaceTime(buildClaimDate).isValid()) {
    buildClaimDate = `${claimYear}-03-01`; // leap dates
  }
  return buildClaimDate;
};

export const convertClaimYearMonthToClaimDate = (claimYear, claimMonth) => {
  return `${claimYear}-${claimMonth}-01`;
};

export const convertClaimDateToClaimYear = claimDate => {
  return claimDate.split('-')[0];
};

export const convertClaimDateToClaimMonth = claimDate => {
  return claimDate.split('-')[1];
};

export const titleCase = (str, autoCapitalize = true) => {
  if (autoCapitalize) {
    str = str
      .toLowerCase()
      .split(' ')
      .map(s => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
  }
  return str;
};

//returns a formatted sub limit for mags/stereo which is relative to the risks good retail value
export function variableSubLimit(ratingValue) {
  const subLimitPercentage = 0.15; //percentage of good retail value for sub limit
  const roundingIncrements = 1000; //Andy want to round up to the nearest thousand
  const subLimitMin = 1000; //Min Sub Limit is $1000
  const subLimitMax = 5000; //Max Sub limit is $5000
  const subLimitAmount = ratingValue * subLimitPercentage;
  const roundedSubLimit = Math.ceil(subLimitAmount / roundingIncrements) * roundingIncrements;
  let formattedSubLimit;
  if (roundedSubLimit <= subLimitMin) {
    formattedSubLimit = '$' + formatMoney(subLimitMin, 0);
  } else if (roundedSubLimit >= subLimitMax) {
    formattedSubLimit = '$' + formatMoney(subLimitMax, 0);
  } else formattedSubLimit = '$' + formatMoney(roundedSubLimit, 0);
  return formattedSubLimit;
}
